import { useCallback, useEffect, useState } from 'react';
import { FaMagnifyingGlass } from 'react-icons/fa6';

import {
    getIssuesByArticleReport,
    getReportIssuesByArticleTotalValue,
    getArticleIssuesDetails,
    getArticleDetailsTotalValue,
} from '../services/ReportsService';
import { exportIssuesByArticleReport, exportIssueArticleDetailsReport } from '../services/ExportService';
import TableContainer from '../components/table/TableContainer';
import { formatCurrency } from '../utils/numbers';
import { AddButton, TableActionButton } from '../styles/StyledComponents';
import { formatDateAndTime } from '../utils/dates';
import { vehicleTypeLabel } from '../utils/translation';
import { useAppContext } from '../contextApi/context';
import { vehicleTypeFilterOptions } from '../constants/vehicleTypes';

const ReportIssuesByArticle = () => {
    // ----- Context
    const { loggedUser } = useAppContext();

    // ----- State
    let [showArticleDetailsTable, setShowArticleDetailsTable] = useState(false);
    let [currentArticle, setCurrentArticle] = useState({});
    let [parentFilters, setParentFilters] = useState([]);
    const [updateTable, setUpdateTable] = useState(false);
    let [tableTotalValue, setTableTotalValue] = useState(null);
    let [detailsTotalValue, setDetailsTotalValue] = useState(null);

    // ----- API Fetch Actions
    const fetchTotalValueData = useCallback(
        async (filters = []) => {
            return await getReportIssuesByArticleTotalValue(loggedUser.token, filters);
        },
        [loggedUser.token]
    );

    const fetchDetailsTotalValueData = useCallback(
        async (filters = [], article) => {
            return await getArticleDetailsTotalValue(loggedUser.token, filters, article);
        },
        [loggedUser.token]
    );

    // ----- Hooks
    useEffect(() => {
        fetchTotalValueData().then((data) => {
            setTableTotalValue(data);
        });
    }, [fetchTotalValueData]);

    // ----- Actions
    const handleShowArticleDetailsTable = (articleId, name) => {
        setShowArticleDetailsTable(false);

        setTimeout(() => {
            setCurrentArticle({ articleId, name });
            setUpdateTable(!updateTable);
            setShowArticleDetailsTable(true);
        }, 100);

        fetchDetailsTotalValueData(parentFilters, { articleId, name }).then((data) => {
            setDetailsTotalValue(data);
        });
    };

    const handleDataAfterFiltering = (filters) => {
        fetchTotalValueData(filters).then((data) => {
            setTableTotalValue(data);
        });

        let parentFilters = JSON.parse(JSON.stringify(filters));
        parentFilters = parentFilters
            .filter(
                (pf) =>
                    pf.columnName === 'plate' ||
                    pf.columnName === 'type' ||
                    pf.columnName === 'brand' ||
                    pf.columnName === 'eventDate' ||
                    pf.columnName === 'issueType'
            )
            .map((f) => {
                if (f.columnName === 'plate') {
                    f.columnName = 'parentPlate';
                }
                if (f.columnName === 'type') {
                    f.columnName = 'parentType';
                }
                if (f.columnName === 'brand') {
                    f.columnName = 'parentBrand';
                }
                return f;
            });
        setParentFilters(parentFilters);

        setShowArticleDetailsTable(false);
    };

    const handleDetailsDataAfterFiltering = (filters) => {
        fetchDetailsTotalValueData(filters, currentArticle).then((data) => {
            setDetailsTotalValue(data);
        });
    };

    // ----- Constants
    const Actions = ({ row }) => (
        <div className='d-flex'>
            <TableActionButton className='btn btn-primary' onClick={() => handleShowArticleDetailsTable(row.id, row.name)}>
                <FaMagnifyingGlass />
                Ver Detalles
            </TableActionButton>
        </div>
    );

    const columns = [
        {
            name: 'Artículo',
            selector: (row) => row.name,
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Cantidad',
            selector: (row) => row.amount,
            sortable: true,
            sortField: 'amount',
        },
        {
            name: 'Valor promedio',
            selector: (row) => `₡${formatCurrency(row.averageValue)}`,
        },
        {
            name: 'Valor total',
            selector: (row) => `₡${formatCurrency(row.totalValue)}`,
            sortable: true,
            sortField: 'totalValue',
        },
        {
            name: 'Acciones',
            width: '150px',
            cell: (row) => <Actions row={row} />,
        },
    ];

    const filters = [
        {
            column: 'name',
            input: 'text',
            label: 'Artículo',
            operation: 'like',
            length: 50,
        },
        {
            column: 'internalCode',
            input: 'text',
            label: 'Código',
            operation: 'like',
            length: 20,
        },
        {
            column: 'brand',
            input: 'text',
            label: 'Marca',
            operation: 'like',
            length: 25,
        },
        {
            column: 'plate',
            input: 'text',
            label: 'Placa',
            operation: 'like',
            length: 20,
        },
        {
            column: 'type',
            input: 'select',
            label: 'Tipo de vehículo',
            options: vehicleTypeFilterOptions,
            operation: 'equal',
        },
        {
            column: 'issueType',
            input: 'text',
            label: 'Tipo de despacho',
            operation: 'like',
            length: 50,
        },
        {
            column: 'totalValue',
            input: 'range',
            label: 'Valor total',
            operation: 'between',
        },
        {
            column: 'eventDate',
            input: 'dates',
            label: 'Fecha de despacho',
            operation: 'between',
        },
    ];

    const exportOptions = {
        handler: exportIssuesByArticleReport,
    };

    const exportDetailstOptions = {
        handler: exportIssueArticleDetailsReport,
    };

    const articleDetailsTableColumns = [
        {
            name: 'Fecha de despacho',
            selector: (row) => (row.eventDate ? formatDateAndTime(row.eventDate) : '--'),
        },
        {
            name: 'Placa',
            selector: (row) => row.plate,
            sortable: true,
            sortField: 'plate',
        },
        {
            name: 'Tipo de vehículo',
            selector: (row) => (row.type ? vehicleTypeLabel(row.type) : '--'),
            sortable: true,
            sortField: 'type',
        },
        {
            name: 'Cantidad',
            selector: (row) => row.amount,
            sortable: true,
            sortField: 'amount',
        },
        {
            name: 'Valor promedio',
            selector: (row) => `₡${formatCurrency(row.averageValue)}`,
        },
        {
            name: 'Valor total',
            selector: (row) => `₡${formatCurrency(row.totalValue)}`,
            sortable: true,
            sortField: 'totalValue',
        },
    ];

    const articleDetailsTableFilters = [
        {
            column: 'plate',
            input: 'text',
            label: 'Placa',
            operation: 'like',
            length: 20,
        },
        {
            column: 'type',
            input: 'select',
            label: 'Tipo',
            options: vehicleTypeFilterOptions,
            operation: 'equal',
        },
    ];

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1 className='mb-5'>Reporte de Despachos por Artículo</h1>

            <TableContainer
                columns={columns}
                fetchDataHandler={getIssuesByArticleReport}
                updateTable={true}
                filters={filters}
                afterFilteringHandler={handleDataAfterFiltering}
                exportOptions={exportOptions}
                totalValue={tableTotalValue}
            />

            {showArticleDetailsTable && (
                <div className='my-5'>
                    <h3>Detalles para artículo: {currentArticle.name}</h3>

                    <div className='d-flex justify-content-end'>
                        <AddButton className='btn btn-primary' onClick={() => setShowArticleDetailsTable(false)}>
                            Ocultar detalles
                        </AddButton>
                    </div>

                    <TableContainer
                        afterFilteringHandler={handleDetailsDataAfterFiltering}
                        columns={articleDetailsTableColumns}
                        fetchDataHandler={getArticleIssuesDetails}
                        updateTable={updateTable}
                        filters={articleDetailsTableFilters}
                        pathVariables={currentArticle}
                        exportOptions={exportDetailstOptions}
                        parentFilters={parentFilters}
                        totalValue={detailsTotalValue}
                    />
                </div>
            )}
        </div>
    );
};

export default ReportIssuesByArticle;
